import React from 'react';

import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { FormattedMessage } from '@techstyle/react-intl';
import { useDomain } from '@techstyle/redux-core';

import { trackGridRecommendations } from '../../actions/products';
import { mobile } from '../../styles';
import { getSectionsAndQueryFromPath } from '../../utils/routing';
import { Component as ProductCell } from '../ProductCell';
import { Component as ProductDetailRecommendations } from '../ProductDetailRecommendations';
import { Component as ProductRecommendations } from '../ProductRecommendations';

import { recommendationsByLocale } from './recommendationsByLocale';

const EmptyGridRecommendationsContainer = styled.div`
  background: ${props => props.theme.colors.marble};

  ${mobile`
    margin-bottom: ${props => props.theme.spacing.moderate}px;
  `}
`;

const EmptyGridRecommendationsMessageContainer = styled.div`
  padding: ${props => props.theme.spacing.moderate}px;
  color: ${props => props.theme.colors.gunmetal};
  ${mobile`
    padding:  ${props => props.theme.spacing.moderate}px ${props =>
    props.theme.spacing.small}px ${props =>
    props.theme.spacing.moderate}px ${props => props.theme.spacing.small}px;
    ${({ theme }) => theme.paragraph.variants.paragraph3.textStyles};
  `}
`;

const EmptyGridRecommendationsMessageTitle = styled.div`
  ${({ theme }) => theme.header.variants.h2.textStyles};
  color: ${props => props.theme.colors.black};
`;

const EmptyGridRecommendationsMessage = styled.div`
  margin-top: ${props => props.theme.spacing.tiny}px;
  ${({ theme }) => theme.paragraph.variants.paragraph3.textStyles};
  color: ${props => props.theme.colors.black};
`;

const EmptyGridRecommendationsTitle = styled.span`
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 0.75px;
  ${({ theme }) => theme.subHeader.variants.subHeading2Uppercase.textStyles};
`;

const StyledProductDetailRecommendations = styled(ProductDetailRecommendations)`
  padding: 0 ${props => props.theme.spacing.tiny}px ${props =>
  props.theme.spacing.medium}px ${props => props.theme.spacing.moderate}px;
  ${ProductRecommendations.Title} {
    text-align: left;
    margin-top: 0;
    margin-bottom: ${props => props.theme.spacing.small}px;
  }
  ${ProductRecommendations.ProductList} {
    padding-bottom 0;
    .slick-slider {
      padding-bottom: 0;
    }
  }
  ${mobile`
    padding: 0 0 ${props => props.theme.spacing.medium}px ${props =>
    props.theme.spacing.small}px !important;
    ${ProductCell.Details} {
      padding-left: 0;
    }
  `}
`;

export default function EmptyGridsRecommendations({
  productsLength,
  filters = {},
}) {
  const dispatch = useDispatch();
  const { tld } = useDomain();
  const { asPath } = useRouter();

  const shouldShowGridsRecommendations = recommendationsByLocale[tld];
  const { root, parentSection, section, subsection } =
    getSectionsAndQueryFromPath(asPath);

  const selectedColors = filters.colorTagIds || filters.color;
  const selectCollections = filters.collectionIds || filters.collection;
  const selectedFabrics = filters.fabricTagIds || filters.fabric;
  const hasNoOtherFilter =
    selectedColors &&
    selectCollections &&
    selectedFabrics &&
    selectedColors.length === 0 &&
    selectCollections.length === 0 &&
    selectedFabrics.length === 0;

  const handleRecommendedProductClick = () => {
    let eventLabel = '';
    const isSalePages = root === 'sale';
    const isNewPages = parentSection === 'new' || parentSection === 'new-vip';
    if (isNewPages) {
      eventLabel = parentSection;
    } else if (isSalePages) {
      eventLabel = root;
    } else {
      eventLabel = subsection || section;
    }
    dispatch(trackGridRecommendations(eventLabel));
  };

  const renderEmptyGridMessageTitle = productsLength => {
    if (productsLength === 0) {
      return (
        <FormattedMessage
          id="site_product_grid.no_products_title"
          defaultMessage="Sorry, Sis!"
        />
      );
    } else if (hasNoOtherFilter) {
      return (
        <FormattedMessage
          id="site_product_grid.no_other_filter_title"
          defaultMessage="Looking for more?"
        />
      );
    }
    return (
      <FormattedMessage
        id="site_product_grid.other_filter_title"
        defaultMessage="Not a lot of results for you."
      />
    );
  };

  const renderEmptyGridMessage = productsLength => {
    if (productsLength === 0) {
      return (
        <FormattedMessage
          id="site_product_grid.no_products_message"
          defaultMessage="We weren’t able to find any results for you. Our styles tend to sell out fast. Check out the hot styles below before they disappear!"
        />
      );
    } else if (hasNoOtherFilter) {
      return (
        <FormattedMessage
          id="site_product_grid.no_other_filter_message"
          defaultMessage="Our styles tend to sell out fast. Check out the hot styles below before they disappear!"
        />
      );
    }
    return (
      <FormattedMessage
        id="site_product_grid.other_filter_message"
        defaultMessage="We weren’t able to find a lot of results for you."
      />
    );
  };

  const renderEmptyGridsRecommendations = () => {
    const selectedJsonData = recommendationsByLocale[tld];
    const additionalParam = section
      ? selectedJsonData[section]
      : selectedJsonData.default;
    return (
      <EmptyGridRecommendationsContainer>
        <EmptyGridRecommendationsMessageContainer data-qa-automation="gridEmpty">
          <EmptyGridRecommendationsMessageTitle>
            {renderEmptyGridMessageTitle(productsLength)}
          </EmptyGridRecommendationsMessageTitle>
          <EmptyGridRecommendationsMessage data-qa-automation="gridSetsEmptyMsg">
            {renderEmptyGridMessage(productsLength)}
          </EmptyGridRecommendationsMessage>
        </EmptyGridRecommendationsMessageContainer>
        <StyledProductDetailRecommendations
          source="grid_recommendation"
          pSource="grid_recommendation"
          additionalParam={additionalParam}
          hidePrice={false}
          hidePromo={false}
          handleProductClick={handleRecommendedProductClick}
          maxProducts={10}
          options={{
            arrows: true,
            dots: false,
            slidesToShow: 4,
            slidesToScroll: 4,
            responsive: [
              {
                // Large Tablet, Small Desktop
                breakpoint: 1023,
                settings: {
                  arrows: true,
                  slidesToShow: 3,
                  slidesToScroll: 3,
                },
              },
              {
                breakpoint: 767,
                settings: {
                  arrows: false,
                  slidesToShow: 1.55, // shows only part of the second item
                  slidesToScroll: 1,
                  swipeToSlide: true,
                },
              },
            ],
          }}
          title={
            <EmptyGridRecommendationsTitle>
              <FormattedMessage
                id="site_pdp.new_arrivals"
                defaultMessage="New Arrivals"
              />
            </EmptyGridRecommendationsTitle>
          }
          variantStyle
          shouldUseBmigComponents={false}
        />
      </EmptyGridRecommendationsContainer>
    );
  };

  return shouldShowGridsRecommendations
    ? renderEmptyGridsRecommendations()
    : null;
}

EmptyGridsRecommendations.propTypes = {
  filters: PropTypes.object,
  productsLength: PropTypes.number,
};

EmptyGridsRecommendations.EmptyGridRecommendationsContainer =
  EmptyGridRecommendationsContainer;
